// ============================================================
// theme.js
// EV Powertrain Simulator — Phase 4 컴포넌트 분리
// COLORS 테마 + 타이포그래피 상수
// ============================================================

// ── COLORS_DARK ──
const COLORS_DARK = {
  bg: "#0d1117", panel: "#161b22", border: "#30363d",
  accent: "#f59e0b", accent2: "#58a6ff", accent3: "#3fb950", accent4: "#f85149",
  accent5: "#bc8cff", text: "#e6edf3", textDim: "#8b949e", textMuted: "#6e7681",
  gridLine: "#21262d", grid: "#21262d", surface: "#21262d",
  battColor: "#f59e0b", invColor: "#58a6ff", motorColor: "#3fb950", gearColor: "#bc8cff",
  coolColor: "#39d3f2",
  roadColor: "#f85149", auxColor: "#6e7681", cuColor: "#ff9500", feColor: "#26c6da", mechColor: "#d2a8ff",
  thermalRed: "#f85149", thermalBlue: "#58a6ff", thermalGreen: "#3fb950", thermalOrange: "#ff9500",
  vehA: "#f85149", vehB: "#58a6ff",
  cardBg: "#161b22",
  isDark: true,
  // 다크 모드 전용 다채색 그래프 팔레트 (가시성 최적화)
  graphPalette: ["#f87171","#60a5fa","#4ade80","#fb923c","#c084fc","#facc15","#2dd4bf","#f472b6","#38bdf8","#a78bfa"],
};
// ── COLORS_LIGHT ──
const COLORS_LIGHT = {
  bg: "#f1f5f9", panel: "#ffffff", border: "#94a3b8",
  accent: "#92400e", accent2: "#1e3a8a", accent3: "#065f46", accent4: "#991b1b",
  accent5: "#5b21b6", text: "#0f172a", textDim: "#1e293b", textMuted: "#374151",
  gridLine: "#94a3b8", grid: "#94a3b8", surface: "#e2e8f0",
  battColor: "#92400e", invColor: "#1e3a8a", motorColor: "#065f46", gearColor: "#5b21b6",
  coolColor: "#0e7490",
  roadColor: "#991b1b", auxColor: "#374151", cuColor: "#0c4a6e", feColor: "#0c4a6e", mechColor: "#581c87",
  thermalRed: "#991b1b", thermalBlue: "#1e3a8a", thermalGreen: "#065f46", thermalOrange: "#9a3412",
  vehA: "#991b1b", vehB: "#1e3a8a",
  cardBg: "#f8fafc",
  isDark: false,
  // 라이트 모드 전용 다채색 그래프 팔레트 (가시성 최적화)
  graphPalette: ["#dc2626","#1d4ed8","#15803d","#c2410c","#6d28d9","#a16207","#0f766e","#be185d","#0369a1","#7c3aed"],
};

const mono = "'JetBrains Mono', monospace";

// ============================================================
// ── SPACING — 4pt 그리드 기반 간격 토큰 (W-1, W-2 대응)
// ──   용도:  gap / padding / margin 값으로 사용
// ──   비표준값(5·7·14px 등) 대신 이 상수만 참조할 것
// ============================================================
const SPACING = {
  xxs:  2,   // 극소 구분선 등 최소 여백
  xs:   4,   // 인라인 배지·아이콘 주변
  sm:   8,   // 기본 항목 간격 (가장 빈번)
  md:  12,   // 섹션 내 그룹 간격
  lg:  16,   // 섹션 간 여백
  xl:  24,   // 카드·패널 간 여백
};

// ============================================================
// ── RADIUS — 용도별 borderRadius 토큰 (C-3 대응)
// ──   의미 없는 1px 차이(5vs6, 7vs8 등) 제거
// ============================================================
const RADIUS = {
  badge:  4,    // 배지·태그·chip
  button: 6,    // 버튼
  card:   8,    // 카드·패널·섹션 박스
  full: "50%",  // 원형 아이콘·아바타
};

// ============================================================
// ── FONT_SIZE — 타이포그래피 계층 토큰 (C-2 대응)
// ──   섹션 제목 / 서브라벨 / 수치 3단계만 사용
// ──   9·13·18px 등 비표준 크기 금지
// ============================================================
const FONT_SIZE = {
  value:    10,  // 수치 라벨, 단위 표기
  sublabel: 11,  // 서브라벨, 범례, 보조 설명
  section:  12,  // 섹션 제목, 카드 헤더
};

// ============================================================
// ── FONT_WEIGHT — fontWeight 토큰 (C-4 대응)
// ──   700(bold)은 HWSafety·RLTab에만 존재 → 500으로 통일
// ============================================================
const FONT_WEIGHT = {
  normal:  400,  // 본문·설명 텍스트
  heading: 500,  // 섹션 헤더·라벨 (600·700 대체)
};

// ============================================================
// ── TAB_WRAPPER — 최상위 탭 래퍼 공통 스타일 (W-4 대응)
// ──   ThermalTabContent(10px 0) · CybersecurityTabContent(16px 0)
// ──   등 불규칙 padding을 이 객체로 대체
// ──   사용법: <div style={TAB_WRAPPER}> ... </div>
// ============================================================
const TAB_WRAPPER = {
  padding: "12px 0",
  display: "flex",
  flexDirection: "column",
  gap: SPACING.lg,   // 16px — 섹션 간 기본 간격
};

// ============================================================
// ── GRID — gridTemplateColumns 공유 상수 (W-5 대응)
// ──   동일 패턴 4종 표기(공백만 다름) → 상수 참조로 통일
// ──   사용법: gridTemplateColumns: GRID.auto2
// ============================================================
const GRID = {
  // 반응형 — 최소 너비별 단열 전환 (숫자는 minmax 하한 px)
  auto160: "repeat(auto-fit, minmax(min(160px, 45vw), 1fr))",
  auto180: "repeat(auto-fit, minmax(min(180px, 45vw), 1fr))",
  auto2:   "repeat(auto-fit, minmax(min(200px, 45vw), 1fr))",  // 기본값
  auto260: "repeat(auto-fit, minmax(min(260px, 45vw), 1fr))",
  // 고정 열
  col2:    "1fr 1fr",
  col3:    "1fr 1fr 1fr",
  col4:    "1fr 1fr 1fr 1fr",
};

export { COLORS_DARK, COLORS_LIGHT, mono, SPACING, RADIUS, FONT_SIZE, FONT_WEIGHT, TAB_WRAPPER, GRID };
